import { forwardRef, type InputHTMLAttributes } from 'react';
import { separarDdiBR, nacionalTelefoneBR } from '@fiodecobre/shared';

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value' | 'type'> & {
  value: string | null | undefined;
  onChange: (digitos: string) => void;
};

/**
 * 🔴 **ESTE CAMPO COMIA DIGITOS ATE 20/08/2026, e nao so na tela.**
 *
 * Ele nasceu pensando em numero NACIONAL (11 digitos: DD + 9 + 8) e cortava em 11
 * nos DOIS sentidos. Mas o banco guarda com DDI: medido em 20/08, **118 dos 200
 * clientes** tem 12 ou 13 digitos. O que acontecia com `5564999943884`:
 *
 *  - **Na tela**: os 11 primeiros digitos viravam `(55) 64999-9438` — o `55` lido
 *    como DDD e os dois ultimos digitos jogados fora. O numero exibido nao existe.
 *  - **Ao EDITAR**: o `onChange` devolvia esses mesmos 11 digitos truncados, entao
 *    encostar no campo **gravava** o numero mutilado. Silenciosamente, sem erro, e
 *    sem volta.
 *
 * O defeito so nao aparecia direto porque o WhatsApp logo ao lado, na mesma tela,
 * usa `formatTelefoneBR` e sempre mostrou certo — entao a divergencia lia como "os
 * dois numeros sao diferentes", e nao como "este campo esta quebrado". Foi assim que
 * o Ruan chegou nele.
 *
 * ✅ Agora o DDI e' **separado pra exibir e reposto pra gravar**, pelo
 * `separarDdiBR` do shared — a mesma peca que o `variantesTelefoneBR` usa pra casar
 * telefone no login do app. ⛔ Nao voltar a cortar por comprimento aqui: quem sabe
 * o que e' DDI e' aquela funcao, e ela existe pra isso.
 */
function formatarNacional(d: string): string {
  if (d.length === 0) return '';
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

export const PhoneInput = forwardRef<HTMLInputElement, Props>(function PhoneInput(
  { value, onChange, ...rest }, ref,
) {
  // O DDI sai do valor ATUAL a cada render: quem tinha 55 continua com 55 depois de
  // editar, e quem nunca teve nao ganha um do nada (seria inventar dado num campo
  // que o operador digitou).
  const { ddi, nacional } = separarDdiBR(value);

  return (
    <input
      ref={ref}
      type="tel"
      inputMode="tel"
      value={formatarNacional(nacional)}
      data-componente="008"
      onChange={(e) => {
        // ⚠️ O corte em 11 aqui e' do numero NACIONAL, e por isso e' seguro — e' o
        // limite real de um telefone brasileiro (DD + 9 digitos). O DDI vem por fora.
        //
        // ⚠️ **O `nacionalTelefoneBR` roda ANTES do corte** (30/08/2026): quem COLA
        // aqui pode trazer DDI ou prefixo de operadora (`021`) grudado, e cortar em
        // 11 pelo fim guardaria os digitos errados. Digitacao normal nao muda.
        const nac = nacionalTelefoneBR(e.currentTarget.value).slice(0, 11);
        // Campo apagado e' ordem de limpar, nao de deixar um `55` orfao pra tras.
        onChange(nac ? ddi + nac : '');
      }}
      {...rest}
    />
  );
});
